import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { 
  Search, Star, User, Trophy, MapPin, Users, 
  ArrowRight, X, Ruler, Scale, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';

import { API } from '../config/api';

const SPORTS = ['Football', 'Basketball', 'Tennis', 'Cricket', 'Rugby', 'Athletics'];

export default function ProAthleteComparison({ childId, childData }) {
  const [selectedSport, setSelectedSport] = useState(childData?.primary_sport || 'Football');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [popularPlayers, setPopularPlayers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  const fetchPopularPlayers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sportsdb/popular-players/${selectedSport}`);
      setPopularPlayers(res.data.players || []);
    } catch (err) {
      console.error('Failed to fetch popular players');
    }
  }, [selectedSport]);

  useEffect(() => {
    if (selectedSport) {
      fetchPopularPlayers();
    }
  }, [selectedSport, fetchPopularPlayers]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sportsdb/search-player`, {
        params: { name: searchQuery, sport: selectedSport }
      });
      setSearchResults(res.data.players || []);
    } catch (err) {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const selectPlayer = async (player) => {
    setSelectedPlayer(player);
    setShowSearch(false);
    setSearchResults([]);
    setSearchQuery('');
    
    // Fetch comparison
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sportsdb/compare`, {
        params: { child_id: childId, player_id: player.id }
      });
      setComparison(res.data);
    } catch (err) {
      toast.error('Failed to load comparison');
    } finally {
      setLoading(false);
    }
  };

  const clearSelection = () => {
    setSelectedPlayer(null);
    setComparison(null);
  };

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="pro-athlete-comparison">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500/20 to-blue-500/20 p-4 border-b border-zinc-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
              <Star className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-white">Compare to Pro Athletes</h3>
              <p className="text-xs text-zinc-400">See how you measure up to the pros</p>
            </div>
          </div>
          
          {/* Sport Selector */}
          <select
            value={selectedSport}
            onChange={(e) => setSelectedSport(e.target.value)}
            className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-white text-sm"
          >
            {SPORTS.map(sport => (
              <option key={sport} value={sport}>{sport}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="p-4">
        {/* Search Toggle */}
        {!selectedPlayer && (
          <Button
            onClick={() => setShowSearch(!showSearch)}
            variant="outline"
            className="w-full mb-4"
          >
            <Search className="h-4 w-4 mr-2" /> 
            {showSearch ? 'Hide Search' : 'Search for a Pro Athlete'}
          </Button>
        )}

        {/* Search Panel */}
        {showSearch && !selectedPlayer && (
          <div className="mb-4 bg-zinc-800/30 rounded-lg p-4">
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search player name..."
                className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
              />
              <Button onClick={handleSearch} disabled={loading}>
                {loading ? 'Searching...' : 'Search'}
              </Button>
            </div>
            
            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {searchResults.map(player => (
                  <button
                    key={player.id}
                    onClick={() => selectPlayer(player)}
                    className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-700/50 transition-colors text-left"
                  >
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-zinc-700">
                      {player.thumb ? (
                        <img src={player.thumb} alt={player.name} className="w-full h-full object-cover" />
                      ) : (
                        <User className="h-5 w-5 text-zinc-500 m-2.5" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-white">{player.name}</div>
                      <div className="text-xs text-zinc-500">{player.team} • {player.position}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Popular Players */}
        {!selectedPlayer && popularPlayers.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-zinc-400 mb-2">Popular {selectedSport} Players</h4>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {popularPlayers.map(player => (
                <button
                  key={player.id}
                  onClick={() => selectPlayer(player)}
                  className="flex-shrink-0 flex flex-col items-center gap-2 p-3 bg-zinc-800/50 rounded-lg hover:bg-zinc-700/50 transition-colors w-24"
                >
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-zinc-700">
                    {player.thumb ? (
                      <img src={player.thumb} alt={player.name} className="w-full h-full object-cover" />
                    ) : (
                      <User className="h-6 w-6 text-zinc-500 m-3" />
                    )}
                  </div>
                  <div className="text-center">
                    <div className="text-xs font-medium text-white truncate w-full">{player.name?.split(' ').pop()}</div>
                    <div className="text-[10px] text-zinc-500 truncate">{player.team}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Comparison View */}
        {selectedPlayer && comparison && (
          <div className="space-y-4">
            {/* Clear Button */}
            <button
              onClick={clearSelection}
              className="text-xs text-zinc-500 hover:text-white flex items-center gap-1"
            >
              <X className="h-3 w-3" /> Choose different player
            </button>

            {/* Comparison Cards */}
            <div className="grid grid-cols-2 gap-4">
              {/* Child Card */}
              <div className="bg-gradient-to-br from-[#02b5ff]/20 to-[#02b5ff]/5 border border-[#02b5ff]/30 rounded-xl p-4">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#02b5ff] to-[#ff025f] mx-auto mb-2 flex items-center justify-center">
                    <User className="h-8 w-8 text-white" />
                  </div>
                  <h4 className="font-bold text-white">{comparison.child.name}</h4>
                  <p className="text-xs text-zinc-400">Rising Star</p>
                </div>
                
                <div className="space-y-2">
                  {comparison.child.height_cm && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-zinc-500 flex items-center gap-1">
                        <Ruler className="h-3 w-3" /> Height
                      </span>
                      <span className="text-sm font-semibold text-white">{comparison.child.height_cm} cm</span>
                    </div>
                  )}
                  {comparison.child.weight_kg && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-zinc-500 flex items-center gap-1">
                        <Scale className="h-3 w-3" /> Weight
                      </span>
                      <span className="text-sm font-semibold text-white">{comparison.child.weight_kg} kg</span>
                    </div>
                  )}
                  {comparison.child.position && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-zinc-500">Position</span>
                      <span className="text-sm text-white">{comparison.child.position}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Pro Card */}
              <div className="bg-gradient-to-br from-yellow-500/20 to-yellow-500/5 border border-yellow-500/30 rounded-xl p-4">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 rounded-full overflow-hidden bg-zinc-700 mx-auto mb-2">
                    {comparison.pro.thumb ? (
                      <img src={comparison.pro.thumb} alt={comparison.pro.name} className="w-full h-full object-cover" />
                    ) : (
                      <Trophy className="h-8 w-8 text-yellow-500 m-4" />
                    )}
                  </div>
                  <h4 className="font-bold text-white">{comparison.pro.name}</h4>
                  <p className="text-xs text-zinc-400">{comparison.pro.team}</p>
                </div>
                
                <div className="space-y-2">
                  {comparison.pro.height_cm && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-zinc-500 flex items-center gap-1">
                        <Ruler className="h-3 w-3" /> Height
                      </span>
                      <span className="text-sm font-semibold text-white">{comparison.pro.height_cm} cm</span>
                    </div>
                  )}
                  {comparison.pro.weight_kg && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-zinc-500 flex items-center gap-1">
                        <Scale className="h-3 w-3" /> Weight
                      </span>
                      <span className="text-sm font-semibold text-white">{comparison.pro.weight_kg} kg</span>
                    </div>
                  )}
                  {comparison.pro.position && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-zinc-500">Position</span>
                      <span className="text-sm text-white">{comparison.pro.position}</span>
                    </div>
                  )}
                  {comparison.pro.nationality && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-zinc-500 flex items-center gap-1">
                        <MapPin className="h-3 w-3" /> From
                      </span>
                      <span className="text-sm text-white">{comparison.pro.nationality}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Comparison Notes */}
            {comparison.comparison_notes?.length > 0 && (
              <div className="bg-zinc-800/30 rounded-lg p-4">
                <h5 className="font-semibold text-white mb-2">Insights</h5>
                <ul className="space-y-1">
                  {comparison.comparison_notes.map((note, i) => (
                    <li key={i} className="text-sm text-zinc-300 flex items-start gap-2">
                      <Star className="h-4 w-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                      {note}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Loading State */}
        {loading && !comparison && (
          <div className="text-center py-8">
            <div className="w-8 h-8 border-2 border-[#02b5ff] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            <p className="text-zinc-500 text-sm">Loading comparison...</p>
          </div>
        )}

        {/* Empty State */}
        {!selectedPlayer && popularPlayers.length === 0 && !showSearch && (
          <div className="text-center py-8">
            <Trophy className="h-10 w-10 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-500">Select a sport and search for pro athletes to compare</p>
          </div>
        )}
      </div>
    </div>
  );
}
